import { faBolt, faCrosshairs, faXmark } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../../Icon";
import { tipAttrs } from "../../tooltip";
import { type Combatant, type EncounterState, weaponInstanceKey, isWeaponLoaded, canUseLimitedWeapon } from "@lancer-vtt/domain";
import { weaponProfiles } from "../attackIntent";
import type { ActionDraft } from "../combatInspector";

/**
 * The **armed-action panel** — the weapon/profile/
 * target picker + "click a target token" prompt shown once an action is armed (the single board-wide
 * {@link ActionDraft} is set for this unit). Extracted from {@link InspectorActionBar} so BOTH the
 * generic mech/pilot inspector and the bespoke NPC control panel render the *same* armed resolution UI
 * over the *same* resolution pipeline (no fork). The extraction is **behavior-preserving**: the generic
 * inspector renders it with the default Tailwind `className` (byte-identical markup), while the NPC
 * panel passes a `.npc-ctl-armed` wrapper to reskin the outer frame to the brutalist surface. Handles
 * both `skirmish` (weapon/profile/accuracy → click a target token) and `activate` (action id →
 * self-activate) off `aim.mode`; the host consumes the next canvas token-click as the target.
 */
export function ArmedActionPanel({
  combatant: c,
  aim,
  setAim,
  onSelfActivate,
  className = "cw-panel",
}: {
  combatant: Combatant;
  /** The armed draft — guaranteed non-null and `attackerId === combatant.id` by the caller. */
  aim: ActionDraft;
  setAim: (a: ActionDraft | null) => void;
  onSelfActivate: (actionId: string) => void;
  /** The outer frame's className — defaults to the generic brutalist `.cw-panel`; NPC passes `.npc-ctl-armed`. */
  className?: string;
}) {
  const weapons = c.base.weapons ?? [];
  const chosenWeapon = aim.mode === "skirmish" ? weapons.find((w) => weaponInstanceKey(w) === aim.weaponId) : undefined;
  const profiles = chosenWeapon ? weaponProfiles(chosenWeapon) : [];

  return (
    <div className={className}>
      <div className="cw-armed-head">
        <span className="cw-armed-title">
          <Icon icon={aim.mode === "skirmish" ? faCrosshairs : faBolt} /> {aim.mode === "skirmish" ? "Skirmish — click a target token" : "Activate — click a target, or self-activate"}
        </span>
        <button onClick={() => setAim(null)} className="cw-x" aria-label="cancel action" {...tipAttrs({ label: "Cancel action", key: "Esc" })}>
          <Icon icon={faXmark} />
        </button>
      </div>

      {aim.mode === "skirmish" ? (
        <div className="cw-armed-row">
          <select
            value={aim.weaponId}
            onChange={(e) => setAim({ ...aim, weaponId: e.target.value, profileId: "" })}
            aria-label="weapon"
            className="cw-field"
          >
            <option value="">{weapons.length ? "default weapon…" : "(unarmed)"}</option>
            {weapons.map((w) => {
              // Recharge (the shared usability predicate's chooser-greying half): a discharged
              // Recharge-tagged weapon is disabled here, mirroring `checkWeaponRecharge`'s
              // resolve-path reject.
              const discharged = Boolean(w.recharge && c.current.affordanceState?.[weaponInstanceKey(w)]?.discharged);
              // Loading (the SAME shared predicate): an unloaded Loading weapon is disabled here,
              // mirroring `checkLoaded`'s resolve-path reject. A mech reloads via its Stabilize
              // secondary; a pilot reloads via its own RELOAD action — the hint picks the right
              // verb by kind.
              const unloaded = Boolean(w.loading && !isWeaponLoaded(c.current.resources, weaponInstanceKey(w)));
              // Limited (the SAME shared predicate): a depleted `Limited {N}` weapon is disabled here, mirroring
              // `checkLimited`'s resolve-path reject. Scene = encounter — no reload verb.
              const limited = Boolean(w.limited !== undefined && !canUseLimitedWeapon(c.current.affordanceState, weaponInstanceKey(w), w.limited));
              const disabled = discharged || unloaded || limited;
              return (
                <option key={weaponInstanceKey(w)} value={weaponInstanceKey(w)} disabled={disabled}>
                  {w.name}
                  {discharged ? ` (discharged — Recharge ${w.recharge}+)` : ""}
                  {unloaded ? ` (unloaded — reload with ${c.kind === "pilot" ? "RELOAD" : "STABILIZE"})` : ""}
                  {limited ? ` (depleted — Limited ${w.limited})` : ""}
                </option>
              );
            })}
          </select>
          {profiles.length > 1 && (
            <select
              value={aim.profileId}
              onChange={(e) => setAim({ ...aim, profileId: e.target.value })}
              aria-label="firing profile"
              className="cw-field"
            >
              {profiles.map((p) => (
                <option key={p.id} value={p.id === chosenWeapon?.id ? "" : p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}
          <label className="cw-armed-label">
            acc/diff
            <input
              type="number"
              value={aim.accuracy}
              onChange={(e) => setAim({ ...aim, accuracy: Number(e.target.value) || 0 })}
              aria-label="accuracy"
              className="cw-field is-num is-center"
            />
          </label>
        </div>
      ) : aim.mode === "activate" ? (
        <div className="cw-armed-row">
          <input
            value={aim.actionId}
            onChange={(e) => setAim({ ...aim, actionId: e.target.value })}
            placeholder="action id (e.g. ms_custom_paint_job)"
            aria-label="action id"
            className="cw-field is-actionid"
          />
          <button
            onClick={() => onSelfActivate(aim.actionId.trim())}
            disabled={!aim.actionId.trim()}
            title="Activate on self (no target)"
            className="cw-cta"
          >
            Self-activate
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The inspector's **Skirmish/Activate action bar**. Opening Skirmish/Activate
 * sets the host's armed {@link ActionDraft} (step 3, the up-front declaration); the canvas then
 * consumes the next click on another token as the target (the host's `fireAt`), with on-canvas
 * sightlines (reach + LoS/cover, off `terrainVisibility`) drawn to each in-reach candidate
 * (`armedSightlines`) — there is no text target list here. For an
 * action with no spatial target, **Self-activate** fires immediately.
 *
 * Both entry buttons honour {@link blockedReason} — the host's affordance mirror of the room's
 * pre-activation disposition, which for this body's kinds is the **reject** arm for every
 * non-GM caller. See that prop's own doc.
 */
export function InspectorActionBar({
  combatant: c,
  state,
  aim,
  setAim,
  onSelfActivate,
  blockedReason,
}: {
  combatant: Combatant;
  state: EncounterState;
  aim: ActionDraft | null;
  setAim: (a: ActionDraft | null) => void;
  onSelfActivate: (actionId: string) => void;
  /**
   * Why this viewer may not arm anything here, or `null` when it may — the host computes it with
   * `genericActionBarBlockReason` (`inspectorEconomy.ts`;
   * `projects/pre-activation-affordances.md` 2.5). Before that item this bar rendered behind the
   * GM-inclusive `canControl` **alone**: Skirmish disabled only on an empty candidate list, and
   * Activate never disabled at all — on a body whose kinds (object/deployable/drone/mine) the
   * room's pre-activation disposition **rejects** outright for a player-attributed caller, because
   * a non-actor has no `perUnitState` entry and so can never be `"ready"`. Greying with the reason
   * rather than hiding the bar mirrors the bespoke bodies' own `boostBlockReason`
   * treatment for Boost: an honest "why not", never a dead click.
   */
  blockedReason: string | null;
}) {
  // Candidate targets: every *other* combatant in the encounter (placed ones carry a distance hint).
  const candidates = state.order.map((id) => state.combatants[id]).filter((t): t is Combatant => !!t && t.id !== c.id);

  // At most one armed action board-wide (point 5): this bar shows its aim UI only
  // when the single app-level `aim` is armed for THIS unit; arming here sets `attackerId: c.id`,
  // which replaces any aim held by another inspector (singular). The `aim === null || …` guard also
  // narrows `aim` to non-null for the armed branch below. Both skirmish and activate declare inline
  // through the shared {@link ArmedActionPanel} (the floating SkirmishHud was retired — the bespoke
  // mech/NPC panels expand their own inline SkirmishChooser instead).
  if (aim === null || aim.attackerId !== c.id) {
    return (
      <div className="cw-actionbar">
        <button
          onClick={() => setAim({ attackerId: c.id, mode: "skirmish", weaponId: "", profileId: "", accuracy: 0 })}
          disabled={blockedReason !== null || candidates.length === 0}
          title={
            blockedReason ??
            (candidates.length === 0 ? "Add another combatant to target" : "Make a weapon attack — then click a target token")
          }
          className="cw-cta"
        >
          <Icon icon={faCrosshairs} /> Skirmish
        </button>
        <button
          onClick={() => setAim({ attackerId: c.id, mode: "activate", actionId: "" })}
          disabled={blockedReason !== null}
          title={blockedReason ?? "Activate an overlay action / system"}
          className="cw-btn"
        >
          <Icon icon={faBolt} /> Activate
        </button>
      </div>
    );
  }

  // Armed → the shared weapon/profile/target panel. The generic inspector gets the default
  // Tailwind frame.
  return <ArmedActionPanel combatant={c} aim={aim} setAim={setAim} onSelfActivate={onSelfActivate} />;
}
